/**
 * MarketingReports — Schedulable reports + Claude-narrated summaries.
 * Wired to `/api/marketing/reports` (May 2026). Create / edit / pause /
 * delete are persisted. The scheduled-run cron itself is not yet wired
 * server-side — `lastRun` shows "Never" until that lands.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import MarketingShell from "@/components/MarketingShell";
import {
  FileText, Calendar, Download, Sparkles, Plus, Mail, Slack,
  Loader2, Trash2, Pause, Play,
} from "lucide-react";
import { toast } from "sonner";
import usePrompt from "@/hooks/usePrompt";
import useConfirm from "@/hooks/useConfirm";

const fmtDate = (iso) => {
  if (!iso) return "Never";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
};

export default function MarketingReports() {
  const { token } = useAuth();
  const prompt = usePrompt();
  const confirm = useConfirm();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/marketing/reports`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(res.data?.items || []);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Two-step prompt — name then schedule — keeps the UX simple and the
  // server contract minimal. Recipients can be edited later via the
  // (future) "Edit" pencil.
  const handleNew = async () => {
    const name = await prompt({
      title: "New scheduled report",
      body: "Give the report a name. You'll be asked for the schedule next.",
      placeholder: "e.g. Weekly marketing digest",
      submitLabel: "Next",
    });
    if (!name || !name.trim()) return;
    const schedule = await prompt({
      title: "When should it run?",
      body: "Free text for now (a cron picker lands later). Examples: 'Every Monday 8:00 AM', '1st of month', 'Daily'.",
      defaultValue: "Every Monday 8:00 AM",
      placeholder: "Schedule",
      submitLabel: "Create",
    });
    if (!schedule || !schedule.trim()) return;
    try {
      await axios.post(
        `${API}/marketing/reports`,
        { name: name.trim(), schedule: schedule.trim(), narrative: true, status: "active" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Scheduled "${name.trim()}"`);
      load();
    } catch {
      toast.error("Could not save report — try again");
    }
  };

  const handleToggleStatus = async (r) => {
    try {
      await axios.put(
        `${API}/marketing/reports/${r.id}`,
        {
          name: r.name,
          schedule: r.schedule,
          recipients: r.recipients || "",
          narrative: r.narrative,
          status: r.status === "active" ? "paused" : "active",
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(r.status === "active" ? "Report paused" : "Report resumed");
      load();
    } catch {
      toast.error("Could not update report");
    }
  };

  const handleDelete = async (r) => {
    const ok = await confirm({
      title: "Delete this report?",
      body: `"${r.name}" will stop running and the schedule will be removed.`,
      confirmLabel: "Delete",
      confirmTone: "danger",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/marketing/reports/${r.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Report deleted");
      load();
    } catch {
      toast.error("Could not delete report");
    }
  };

  if (loading) {
    return (
      <MarketingShell title="Reports" subtitle="Loading…">
        <div style={{ display: "flex", justifyContent: "center", padding: 80 }} data-testid="reports-loading">
          <Loader2 style={{ width: 24, height: 24, animation: "spin 1s linear infinite", color: "var(--blue)" }} />
        </div>
      </MarketingShell>
    );
  }

  return (
    <MarketingShell
      title="Reports"
      subtitle={reports.length === 0 ? "No reports scheduled yet" : `${reports.length} scheduled`}
      actions={
        <button className="btn btn-primary" onClick={handleNew} data-testid="new-report-btn">
          <Plus style={{ width: 13, height: 13 }} /> New report
        </button>
      }
    >
      <div data-testid="reports-page">
        <div className="ai-banner" style={{ marginBottom: 22 }}>
          <div className="ai-pill"><Sparkles style={{ width: 11, height: 11 }} /> Analyst Agent</div>
          <p style={{ fontSize: 12.5 }}>
            {reports.length === 0
              ? "No reports scheduled yet. Click \u201CNew report\u201D to schedule a weekly marketing digest, pipeline sourced report, or GEO/citation brief — each summary is narrated by the Analyst Agent."
              : `${reports.filter((r) => r.status === "active").length} active · ${reports.filter((r) => r.status === "paused").length} paused. Each summary is narrated by the Analyst Agent.`}
          </p>
        </div>

        <h3 className="card-title" style={{ marginBottom: 12 }}>Scheduled reports</h3>
        <div className="mkt-table-wrap">
          {reports.length === 0 ? (
            <div data-testid="reports-empty" style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12.5 }}>
              No scheduled reports yet. Click "New report" to create one.
            </div>
          ) : (
          <table className="mkt-table">
            <thead>
              <tr>{["Report", "Schedule", "Recipients", "Last run", "Status", ""].map(h => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} data-testid={`report-${r.id}`}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <FileText style={{ width: 14, height: 14, color: "var(--blue)" }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 540 }}>{r.name}</div>
                        {r.narrative && (
                          <span className="tag tag-blue" style={{ marginTop: 3, display: "inline-flex", alignItems: "center", gap: 3 }}>
                            <Sparkles style={{ width: 9, height: 9 }} /> Claude narrative
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                    <Calendar style={{ width: 11, height: 11, marginRight: 4, display: "inline" }} />
                    {r.schedule}
                  </td>
                  <td style={{ fontSize: 11.5, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                    {(r.recipients || "").includes("#")
                      ? <Slack style={{ width: 11, height: 11, display: "inline" }} />
                      : <Mail style={{ width: 11, height: 11, display: "inline" }} />}
                    &nbsp;{r.recipients || "—"}
                  </td>
                  <td style={{ fontSize: 12, color: "var(--text-secondary)" }}>{fmtDate(r.last_run_at)}</td>
                  <td>
                    <span className={`tag ${r.status === "active" ? "tag-green" : "tag-amber"}`}>
                      {r.status === "active" ? "Active" : "Paused"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button
                        className="btn"
                        style={{ height: 26, padding: "0 8px", fontSize: 11 }}
                        onClick={() => handleToggleStatus(r)}
                        data-testid={`report-${r.id}-toggle`}
                        title={r.status === "active" ? "Pause" : "Resume"}
                      >
                        {r.status === "active"
                          ? <Pause style={{ width: 11, height: 11 }} />
                          : <Play style={{ width: 11, height: 11 }} />}
                      </button>
                      <button
                        className="btn"
                        style={{ height: 26, padding: "0 8px", fontSize: 11 }}
                        onClick={() => toast("Run-now wires up when the report cron lands")}
                        data-testid={`report-${r.id}-run-now`}
                        title="Run now"
                      >
                        <Download style={{ width: 11, height: 11 }} />
                      </button>
                      <button
                        className="btn"
                        style={{ height: 26, padding: "0 8px", fontSize: 11, color: "var(--error)" }}
                        onClick={() => handleDelete(r)}
                        data-testid={`report-${r.id}-delete`}
                        title="Delete"
                      >
                        <Trash2 style={{ width: 11, height: 11 }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      </div>
    </MarketingShell>
  );
}
